import React from 'react';
import { RootState } from '@/store';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { USER_ROLE } from '@/config/constants';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import { setInvestorMode, setRefinanceEnabled } from '@/store/slices/modeSlice';

export type ModeToggleProps = {
  /** When true, renders as a padded panel (for use inside another Card column). */
  embedded?: boolean;
};

export const ModeToggle = ({ embedded = false }: ModeToggleProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: user } = useGetCurrentUserQuery();
  const refinanceEnabled = useSelector(
    (state: RootState) => state.mode.refinanceEnabled
  );
  const isInvestorMode = useSelector(
    (state: RootState) => state.mode.isInvestorMode
  );

  const isBuyerLike =
    user?.role === USER_ROLE.BUYER || user?.role === USER_ROLE.USER;

  const handleRefinanceToggle = (enabled: boolean) => {
    dispatch(setRefinanceEnabled(enabled));
    if (enabled) {
      navigate('/refinance');
    }
  };

  const handleInvestorModeChange = (isInvestor: boolean) => {
    dispatch(setInvestorMode(isInvestor));
  };

  const workflowChips = (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        justifyContent: 'flex-end',
      }}
    >
      <Chip label="Purchase" color="primary" size="small" />
      {refinanceEnabled && <Chip label="Refinance" color="info" size="small" />}
      {isInvestorMode && <Chip label="Investor" color="success" size="small" />}
    </Box>
  );

  const panelSx = {
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.1
    )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    borderRadius: 2,
  } as const;

  const inner = (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h6" fontWeight={600} color="text.primary">
          Loan workflows
        </Typography>
        {workflowChips}
      </Box>

      {isBuyerLike && (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={refinanceEnabled}
                onChange={(e) => handleRefinanceToggle(e.target.checked)}
                color="secondary"
              />
            }
            label={
              <Box sx={{ ml: 1 }}>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color="text.primary"
                >
                  Enable Refinance
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Opens your refinance hub. Add or edit properties, run
                  scenarios, and track rates—purchase workflows remain
                  unchanged.
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', mb: 1 }}
          />
          <Button
            variant="outlined"
            size="small"
            disabled={!refinanceEnabled}
            onClick={() => navigate('/refinance/intake')}
            sx={{ ml: { xs: 0, sm: 5.5 }, mb: 1 }}
          >
            New refinance intake
          </Button>
        </>
      )}

      <Divider sx={{ my: 2 }} />

      <FormControlLabel
        control={
          <Switch
            checked={isInvestorMode}
            onChange={(e) => handleInvestorModeChange(e.target.checked)}
            color="success"
          />
        }
        label={
          <Box sx={{ ml: 1 }}>
            <Typography variant="body2" fontWeight={500} color="text.primary">
              Investor
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Enable access to investment-specific loan programs (DSCR, Bridge,
              Commercial, etc.)
            </Typography>
          </Box>
        }
        sx={{ alignItems: 'flex-start' }}
      />
    </>
  );

  if (embedded) {
    return <Box sx={{ ...panelSx, p: 2.5, height: '100%' }}>{inner}</Box>;
  }

  return (
    <Card sx={{ ...panelSx, mb: 3 }}>
      <CardContent>{inner}</CardContent>
    </Card>
  );
};
